// One audio at a time, app-wide. A capture-phase `play` listener on document
// (the event does not bubble) sees every media start — React-rendered,
// native `controls`, and <audio> injected into author HTML alike — and pauses
// every other audio. React-controlled players register a pause callback
// instead of being paused through the DOM, so their playing state stays in
// sync with the element.

export interface MediaController {
  /** Pause this player through its own state, preserving position. */
  pause: () => void;
  /** True when `node` belongs to this player (its container subtree). */
  contains: (node: Node) => boolean;
}

const controllers = new Set<MediaController>();
let installed = false;

// Video is never force-paused: APVideoPlayer counts a fresh play on every
// playing→true transition, so an auto-pause would consume a student's exam
// play chance. Starting a video still silences audio.
const PAUSABLE_SELECTOR = 'audio';

export const MEDIA_EXCLUSIVITY_ATTR = 'data-media-exclusivity';
/**
 * ONE-WAY exemption for continuous background audio. A yielding track never
 * demands silence — starting it leaves every other player alone — but it is
 * still a target: any other audio starting pauses it, with no auto-resume.
 */
export const MEDIA_EXCLUSIVITY_YIELD = 'yield';
const yieldsToOthers = (element: Element): boolean =>
  element.getAttribute(MEDIA_EXCLUSIVITY_ATTR) === MEDIA_EXCLUSIVITY_YIELD;

/** Pause every audio except the one `source` belongs to. */
export const pauseOtherAudio = (source: Node | null): void => {
  if (typeof document === 'undefined') return;
  const owner = source
    ? Array.from(controllers).find((controller) => controller.contains(source))
    : undefined;

  controllers.forEach((controller) => {
    if (controller !== owner) controller.pause();
  });

  document.querySelectorAll<HTMLMediaElement>(PAUSABLE_SELECTOR).forEach((element) => {
    if (element === source || element.paused) return;
    if (owner?.contains(element)) return;
    element.pause();
  });
};

const handlePlay = (event: Event): void => {
  const target = event.target;
  // Muted elements make no sound (webcam / recorder live previews) — skip.
  if (!(target instanceof HTMLMediaElement) || target.muted) return;
  if (yieldsToOthers(target)) return;
  pauseOtherAudio(target);
};

/** Idempotent — called once at boot and defensively on every registration. */
export const installMediaExclusivity = (): void => {
  if (installed || typeof document === 'undefined') return;
  document.addEventListener('play', handlePlay, true);
  installed = true;
};

/** Register a React-controlled player; returns the unregister callback. */
export const registerMediaController = (controller: MediaController): (() => void) => {
  installMediaExclusivity();
  controllers.add(controller);
  return () => {
    controllers.delete(controller);
  };
};
